import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { billingAPI, phoneSystemAPI, aiActionsAPI } from '../lib/api';
import {
  CreditCard, Check, Loader2, ExternalLink,
  CheckCircle, Clock, AlertTriangle, XCircle,
  Sparkles, Gift, ArrowUp, ArrowDown,
  Phone, PhoneIncoming, PhoneOutgoing, MessageSquare, Zap,
  Receipt, CircleDollarSign, CircleX, Eye, CalendarDays,
  Boxes, Users, Shield, Brain
} from 'lucide-react';
import { toast } from 'sonner';

const FAMILY_ORDER = { inventory: ['inventory_starter', 'inventory_growth', 'inventory_pro'], crm: ['crm_starter', 'crm_growth', 'crm_pro'] };
const fmt = (cents) => `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;

// ── Shared row ──
const Row = ({ label, value, bold }) => (
  <div className={`flex justify-between px-4 py-2.5 ${bold ? 'font-semibold' : ''}`}>
    <span className={bold ? '' : 'text-muted-foreground'}>{label}</span>
    <span className={bold ? '' : 'font-medium'}>{value}</span>
  </div>
);

// ── Phone Add-on Drawer ──
const PhoneAddonDrawer = ({ open, onClose, summary, billingHistory, historyLoading, shadowData, onDisable, disabling }) => {
  if (!summary) return null;

  const pct = summary.included_minutes > 0
    ? Math.min(100, Math.round((summary.minutes_used / summary.included_minutes) * 100))
    : 0;

  const fmtDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const fmtCycleRange = () => {
    if (!summary.billing_cycle_start) return '';
    const s = new Date(summary.billing_cycle_start);
    const e = new Date(summary.billing_cycle_end || summary.billing_cycle_start);
    return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${e.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };

  const isOverageShadow = !summary.overage_billing_live;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="sm:max-w-md w-full p-0 flex flex-col" data-testid="phone-addon-drawer">
        <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Phone className="w-4.5 h-4.5 text-primary" />
            </div>
            <div>
              <SheetTitle className="text-base">Phone System</SheetTitle>
              <SheetDescription className="text-xs">Manage plan and review usage</SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          <Tabs defaultValue="overview" className="h-full flex flex-col">
            <div className="px-6 pt-4 shrink-0">
              <TabsList className="w-full" data-testid="phone-detail-tabs">
                <TabsTrigger value="overview" className="flex-1">Plan Overview</TabsTrigger>
                <TabsTrigger value="usage" className="flex-1">Usage</TabsTrigger>
                <TabsTrigger value="history" className="flex-1">History</TabsTrigger>
              </TabsList>
            </div>

            {/* ── Plan Overview ── */}
            <TabsContent value="overview" className="px-6 py-4 space-y-5 flex-1">
              <div className="flex items-center gap-2">
                <Badge className={summary.enabled
                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                  : 'bg-gray-100 text-gray-600'
                }>
                  {summary.enabled ? 'Active' : 'Inactive'}
                </Badge>
                {summary.tier_name && <Badge variant="outline" className="text-[10px]">{summary.tier_name}</Badge>}
              </div>

              {/* Setup CTA */}
              {summary.enabled && !summary.setup_complete && (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 flex items-center gap-3" data-testid="setup-cta">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
                    <Phone className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-amber-900">Setup Required</p>
                    <p className="text-xs text-amber-700">Get phone numbers and configure your call flow to start receiving calls.</p>
                  </div>
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white shrink-0" onClick={() => window.location.href = '/phone-setup'} data-testid="complete-setup-btn">Complete Setup</Button>
                </div>
              )}

              <div className="rounded-lg border divide-y text-sm">
                <Row label="Monthly base" value={fmt(summary.base_price_monthly)} />
                <Row label="Included lines" value={`${summary.included_lines} lines`} />
                <Row label="Included minutes" value={`${summary.included_minutes} min / month`} />
                <Row label="Extra line price" value={`${fmt(summary.extra_line_price_monthly)} / line / mo`} />
                <Row label="Overage rate" value={`${fmt(summary.overage_per_minute)} / minute`} />
              </div>

              {/* Billing status indicators */}
              <div className="rounded-lg border p-3 space-y-2 text-xs" data-testid="billing-status-indicators">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span className="text-muted-foreground">Base add-on + extra lines — <span className="font-medium text-foreground">billed live</span></span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${isOverageShadow ? 'bg-amber-400' : 'bg-emerald-500'}`} />
                  <span className="text-muted-foreground">
                    Minute overages — <span className="font-medium text-foreground">{isOverageShadow ? 'tracked only (not billed)' : 'billed live'}</span>
                  </span>
                </div>
              </div>

              {summary.enabled && (
                <div className="pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:text-destructive w-full"
                    onClick={onDisable}
                    disabled={disabling}
                    data-testid="disable-phone-addon-btn"
                  >
                    {disabling ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                    Disable Phone System
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* ── Usage ── */}
            <TabsContent value="usage" className="px-6 py-4 space-y-4 flex-1" data-testid="phone-usage-tab">
              {/* Billing cycle header */}
              {summary.billing_cycle_start && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <CalendarDays className="w-3 h-3" />
                  <span>Current cycle: {fmtCycleRange()}</span>
                </div>
              )}

              {/* Minutes */}
              <div className="rounded-lg border p-4 space-y-2.5">
                <div className="flex items-center justify-between text-sm font-medium">
                  <span className="flex items-center gap-1.5"><PhoneOutgoing className="w-3.5 h-3.5 text-muted-foreground" />Minutes</span>
                  <span>{summary.minutes_used} / {summary.included_minutes}</span>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-amber-500' : 'bg-primary'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{summary.minutes_remaining} remaining</span>
                  {summary.overage_minutes > 0 && (
                    <span className="text-amber-600 font-medium">
                      {summary.overage_minutes} overage
                    </span>
                  )}
                </div>
              </div>

              {/* Overage card — shows when there are overages */}
              {summary.overage_minutes > 0 && (
                <div className={`rounded-lg border p-4 space-y-2 ${isOverageShadow ? 'border-amber-200 bg-amber-50/50' : 'border-red-200 bg-red-50/50'}`} data-testid="shadow-overage-card">
                  <div className="flex items-center gap-2">
                    <Eye className={`w-3.5 h-3.5 ${isOverageShadow ? 'text-amber-600' : 'text-red-600'}`} />
                    <span className={`text-sm font-medium ${isOverageShadow ? 'text-amber-800' : 'text-red-800'}`}>
                      {isOverageShadow ? 'Projected Overage' : 'Minute Overage'}
                    </span>
                    {isOverageShadow ? (
                      <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0">Not billed yet</Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700 text-[10px] px-1.5 py-0">Billed at cycle end</Badge>
                    )}
                  </div>
                  <div className={`grid grid-cols-2 gap-x-4 gap-y-1 text-xs ${isOverageShadow ? '' : ''}`}>
                    <span className={isOverageShadow ? 'text-amber-700' : 'text-red-700'}>Overage minutes</span>
                    <span className={`text-right font-medium ${isOverageShadow ? 'text-amber-900' : 'text-red-900'}`}>{summary.overage_minutes}</span>
                    <span className={isOverageShadow ? 'text-amber-700' : 'text-red-700'}>Rate</span>
                    <span className={`text-right font-medium ${isOverageShadow ? 'text-amber-900' : 'text-red-900'}`}>{fmt(summary.overage_per_minute)} / min</span>
                    <span className={isOverageShadow ? 'text-amber-700' : 'text-red-700'}>Projected charge</span>
                    <span className={`text-right font-semibold ${isOverageShadow ? 'text-amber-900' : 'text-red-900'}`}>{fmt(summary.estimated_overage_charge)}</span>
                  </div>
                  {isOverageShadow ? (
                    <p className="text-[10px] text-amber-600/80 leading-relaxed pt-1">
                      Overage charges are being tracked but will not appear on your invoice yet.
                    </p>
                  ) : (
                    <p className="text-[10px] text-red-600/80 leading-relaxed pt-1">
                      This charge will be added to your invoice at the end of the current billing cycle.
                    </p>
                  )}
                </div>
              )}

              {/* Lines */}
              <div className="rounded-lg border p-4 space-y-1.5">
                <div className="flex items-center justify-between text-sm font-medium">
                  <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-muted-foreground" />Lines</span>
                  <span>{summary.active_lines} / {summary.included_lines}</span>
                </div>
                {summary.extra_lines > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {summary.extra_lines} extra line{summary.extra_lines > 1 ? 's' : ''} &middot; {fmt(summary.estimated_extra_line_charge)}/mo
                  </p>
                )}
                {summary.active_lines === 0 && (
                  <p className="text-xs text-muted-foreground">No provisioned lines yet</p>
                )}
              </div>

              {/* Estimated charges */}
              <div className="rounded-lg border divide-y text-sm">
                <Row label="Base charge" value={fmt(summary.estimated_base_charge)} />
                {summary.extra_lines > 0 && (
                  <Row label={`Extra lines (${summary.extra_lines})`} value={fmt(summary.estimated_extra_line_charge)} />
                )}
                {summary.overage_minutes > 0 && (
                  <div className="flex justify-between px-4 py-2.5 text-amber-700 bg-amber-50/30">
                    <span className="flex items-center gap-1.5">
                      {isOverageShadow && <Eye className="w-3 h-3" />}
                      Overage ({summary.overage_minutes} min)
                    </span>
                    <span className="font-medium">{fmt(summary.estimated_overage_charge)}</span>
                  </div>
                )}
                <Row label="Estimated total" value={fmt(summary.estimated_total)} bold />
              </div>

              {/* Shadow billing debug/status */}
              {shadowData?.shadow_record && (
                <div className="rounded-lg border border-dashed border-muted-foreground/20 p-3 space-y-1.5" data-testid="shadow-debug-panel">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <Eye className="w-3 h-3" />
                    Shadow Billing Status
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                    <span>Status</span>
                    <span className="text-right font-mono">{shadowData.shadow_record.status}</span>
                    <span>Used minutes</span>
                    <span className="text-right font-mono">{shadowData.shadow_record.used_minutes}</span>
                    <span>Overage minutes</span>
                    <span className="text-right font-mono">{shadowData.shadow_record.overage_minutes}</span>
                    <span>Projected charge</span>
                    <span className="text-right font-mono">{fmt(shadowData.shadow_record.projected_charge)}</span>
                    <span>Mode</span>
                    <span className="text-right font-mono">{shadowData.overage_billing_live ? 'live' : 'shadow'}</span>
                  </div>
                </div>
              )}

              <p className="text-[11px] text-muted-foreground leading-relaxed">
                {isOverageShadow
                  ? 'Base and extra line charges are billed live. Minute overage charges are tracked in shadow mode and will not be invoiced yet.'
                  : 'All charges are billed live.'
                }
              </p>
            </TabsContent>

            {/* ── Billing History ── */}
            <TabsContent value="history" className="px-6 py-4 space-y-3 flex-1" data-testid="phone-billing-history-tab">
              {historyLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : billingHistory.length === 0 ? (
                <div className="text-center py-8 space-y-2">
                  <Receipt className="w-8 h-8 text-muted-foreground/40 mx-auto" />
                  <p className="text-sm text-muted-foreground">No billing events yet</p>
                  <p className="text-xs text-muted-foreground/70">
                    Payment records will appear here after your first billing cycle.
                  </p>
                </div>
              ) : (
                <div className="space-y-2" data-testid="phone-billing-history-list">
                  {billingHistory.map((ev) => (
                    <div
                      key={ev.id}
                      className="rounded-lg border p-3 flex items-start gap-3"
                      data-testid={`billing-event-${ev.id}`}
                    >
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                        ev.event_type === 'paid'
                          ? 'bg-emerald-100 text-emerald-600'
                          : 'bg-red-100 text-red-600'
                      }`}>
                        {ev.event_type === 'paid'
                          ? <CircleDollarSign className="w-3.5 h-3.5" />
                          : <CircleX className="w-3.5 h-3.5" />
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium truncate">
                            {ev.event_type === 'paid' ? 'Payment received' : 'Payment failed'}
                          </span>
                          <span className={`text-sm font-semibold tabular-nums shrink-0 ${
                            ev.event_type === 'paid' ? 'text-foreground' : 'text-red-600'
                          }`}>
                            {fmt(ev.total_amount)}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{ev.description}</p>
                        <p className="text-[11px] text-muted-foreground/60 mt-1">{fmtDate(ev.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ── Main Billing Page ──
export const Billing = () => {
  const { hasPerm, isOwner } = useAuth();
  const location = useLocation();
  const redirectState = location.state;
  const [loading, setLoading] = useState(true);
  const [pricing, setPricing] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [changePlanLoading, setChangePlanLoading] = useState(null);

  // Phone addon
  const [phoneConfig, setPhoneConfig] = useState(null);
  const [phoneSummary, setPhoneSummary] = useState(null);
  const [phoneEnabled, setPhoneEnabled] = useState(false);
  const [phoneEnabling, setPhoneEnabling] = useState(false);
  const [phoneDisabling, setPhoneDisabling] = useState(false);
  const [phoneDrawerOpen, setPhoneDrawerOpen] = useState(false);
  const [phoneBillingHistory, setPhoneBillingHistory] = useState([]);
  const [phoneHistoryLoading, setPhoneHistoryLoading] = useState(false);
  const [phoneShadowData, setPhoneShadowData] = useState(null);
  // StockBot / AI Actions add-on
  const [aiStatus, setAiStatus] = useState(null);
  const [aiEnabling, setAiEnabling] = useState(false);
  const [selectedFamily, setSelectedFamily] = useState('crm');
  const [billingInterval, setBillingInterval] = useState('monthly');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pricingRes, subRes] = await Promise.all([
        billingAPI.getPricing(),
        billingAPI.getSubscription(),
      ]);
      setPricing(pricingRes.data);
      setSubscription(subRes.data);

      try {
        const [configRes, summaryRes] = await Promise.all([
          phoneSystemAPI.billingConfig(),
          phoneSystemAPI.billingSummary(),
        ]);
        setPhoneConfig(configRes.data);
        setPhoneSummary(summaryRes.data);
        setPhoneEnabled(summaryRes.data.enabled);
      } catch {
        // CRM not entitled — phone section won't render
      }
      
      // StockBot / AI Actions status
      try {
        const aiRes = await aiActionsAPI.getStatus();
        setAiStatus(aiRes.data);
      } catch {
        // AI Actions not available or not entitled — card won't show enabled state
        setAiStatus({ enabled: false });
      }
    } catch {
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (tierId) => {
    setCheckoutLoading(tierId);
    try {
      const interval = billingInterval; // 'monthly' or 'yearly'
      const response = await billingAPI.createCheckout(tierId, window.location.origin, interval);
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setCheckoutLoading(null);
    }
  };

  const handleChangePlan = async (tierId, interval) => {
    // interval should be 'month' or 'year' for the backend
    const backendInterval = interval === 'yearly' ? 'year' : interval === 'monthly' ? 'month' : interval;
    setChangePlanLoading(`${tierId}_${backendInterval}`);
    try {
      const response = await billingAPI.changePlan(tierId, backendInterval);
      toast.success(response.data.message);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change plan');
    } finally {
      setChangePlanLoading(null);
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const response = await billingAPI.createPortal(`${window.location.origin}/billing`);
      window.location.href = response.data.portal_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open billing portal');
      setPortalLoading(false);
    }
  };

  const handleEnablePhone = async () => {
    setPhoneEnabling(true);
    try {
      await phoneSystemAPI.enableAddon();
      toast.success('Phone System enabled');
      await fetchData();
      setPhoneDrawerOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enable Phone System');
    } finally {
      setPhoneEnabling(false);
    }
  };

  const handleDisablePhone = async () => {
    if (!window.confirm('Disable Phone System? Numbers remain but calling/SMS features will be paused.')) return;
    setPhoneDisabling(true);
    try {
      await phoneSystemAPI.disableAddon();
      toast.success('Phone System disabled');
      setPhoneDrawerOpen(false);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable');
    } finally {
      setPhoneDisabling(false);
    }
  };

  const handleOpenPhoneDrawer = async () => {
    setPhoneDrawerOpen(true);
    setPhoneHistoryLoading(true);
    try {
      const [histRes, shadowRes] = await Promise.all([
        phoneSystemAPI.billingHistory(),
        phoneSystemAPI.overageShadow(),
      ]);
      setPhoneBillingHistory(histRes.data);
      setPhoneShadowData(shadowRes.data);
    } catch {
      setPhoneBillingHistory([]);
      setPhoneShadowData(null);
    } finally {
      setPhoneHistoryLoading(false);
    }
  };

  // --- StockBot / AI Actions handlers ---
  const handleAiEnable = async (tier) => {
    setAiEnabling(true);
    try {
      await aiActionsAPI.enable(tier);
      const res = await aiActionsAPI.getStatus();
      setAiStatus(res.data);
      toast.success(`StockBot ${tier === 'pro' ? 'Pro' : 'Lite'} enabled`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to enable StockBot');
    } finally {
      setAiEnabling(false);
    }
  };

  const handleAiChangeTier = async (newTier) => {
    setAiEnabling(true);
    try {
      await aiActionsAPI.updateTier(newTier);
      const res = await aiActionsAPI.getStatus();
      setAiStatus(res.data);
      toast.success(`Switched to StockBot ${newTier === 'pro' ? 'Pro' : 'Lite'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change plan');
    } finally {
      setAiEnabling(false);
    }
  };

  const handleAiDisable = async () => {
    setAiEnabling(true);
    try {
      await aiActionsAPI.disable();
      setAiStatus({ enabled: false });
      toast.success('StockBot disabled');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disable StockBot');
    } finally {
      setAiEnabling(false);
    }
  };

  const getStatusBadge = (status, hasSubscription) => {
    const styles = {
      trial: { icon: Clock, color: 'bg-blue-100 text-blue-700', label: 'Trial' },
      active: { icon: CheckCircle, color: 'bg-green-100 text-green-700', label: 'Active' },
      past_due: { icon: AlertTriangle, color: 'bg-yellow-100 text-yellow-700', label: 'Past Due' },
      canceled: { icon: XCircle, color: 'bg-red-100 text-red-700', label: 'Canceled' },
      incomplete: { icon: AlertTriangle, color: 'bg-orange-100 text-orange-700', label: 'Incomplete' },
    };
    if (!hasSubscription && status !== 'trial') {
      return <Badge className="bg-gray-100 text-gray-700 gap-1"><XCircle className="w-3 h-3" />No Active Plan</Badge>;
    }
    const config = styles[status] || styles.trial;
    const Icon = config.icon;
    return <Badge className={`${config.color} gap-1`}><Icon className="w-3 h-3" />{config.label}</Badge>;
  };

  const getTierAction = (tierId) => {
    const currentTier = subscription?.plan_tier_id;
    if (!currentTier) return 'subscribe';
    // Match current plan (handle legacy keys too)
    const legacyMap = { starter: 'inventory_starter', growth: 'inventory_growth', pro: 'inventory_pro' };
    const resolvedCurrent = legacyMap[currentTier] || currentTier;
    
    // Normalize intervals: frontend uses 'monthly'/'yearly', backend returns 'month'/'year'
    const currentInterval = subscription?.plan_interval; // 'month' or 'year'
    const viewingInterval = billingInterval === 'yearly' ? 'year' : 'month';
    
    const isSameTier = (tierId === resolvedCurrent || tierId === currentTier);
    
    if (isSameTier && viewingInterval === currentInterval) return 'current';
    if (isSameTier && viewingInterval !== currentInterval) return 'switch_interval';
    
    // Determine upgrade/downgrade within the same family
    const currentFamily = resolvedCurrent.startsWith('crm_') ? 'crm' : 'inventory';
    const targetFamily = tierId.startsWith('crm_') ? 'crm' : 'inventory';
    const familyTiers = FAMILY_ORDER[targetFamily];
    if (currentFamily !== targetFamily) return 'subscribe'; // cross-family = new subscription
    const currentIdx = familyTiers.indexOf(resolvedCurrent);
    const targetIdx = familyTiers.indexOf(tierId);
    return targetIdx > currentIdx ? 'upgrade' : 'downgrade';
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          {redirectState?.entitlement_required && (
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="upgrade-notice">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-900">Feature requires a plan upgrade</p>
                <p className="text-sm text-amber-700 mt-0.5">
                  The <span className="font-medium">{redirectState.entitlement_required.toUpperCase()}</span> module
                  is not included in your current plan. Upgrade below to unlock access.
                </p>
              </div>
            </div>
          )}
          {redirectState?.addon_required && (
            <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="addon-notice">
              <AlertTriangle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-blue-900">Feature requires an add-on</p>
                <p className="text-sm text-blue-700 mt-0.5">
                  {redirectState.addon_required === 'phone_system'
                    ? 'The Contact Center requires the Phone System add-on. Enable it below to get started.'
                    : `The ${redirectState.addon_required.replace(/_/g, ' ')} add-on is required. Enable it below.`}
                </p>
              </div>
            </div>
          )}
          <div className="flex items-center justify-center min-h-[400px]">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        </div>
      </Layout>
    );
  }

  const hasActiveSubscription = subscription?.has_subscription;
  const hasAddonBypass = subscription?.allow_addons_without_subscription;
  const canEnableAddons = hasActiveSubscription || hasAddonBypass;

  return (
    <Layout>
      <div className="space-y-8" data-testid="billing-page">
        {/* ═══ Upgrade Notice (shown when redirected from a blocked route) ═══ */}
        {redirectState?.entitlement_required && (
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="upgrade-notice">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-900">Feature requires a plan upgrade</p>
              <p className="text-sm text-amber-700 mt-0.5">
                The <span className="font-medium">{redirectState.entitlement_required.toUpperCase()}</span> module
                is not included in your current plan. Upgrade below to unlock access.
              </p>
            </div>
          </div>
        )}
        {redirectState?.addon_required && (
          <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="addon-notice">
            <AlertTriangle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-900">Feature requires an add-on</p>
              <p className="text-sm text-blue-700 mt-0.5">
                {redirectState.addon_required === 'phone_system'
                  ? 'The Contact Center requires the Phone System add-on. Enable it below to get started.'
                  : `The ${redirectState.addon_required.replace(/_/g, ' ')} add-on is required. Enable it below.`}
              </p>
            </div>
          </div>
        )}
        {/* ═══ Header ═══ */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
            <CreditCard className="w-5 h-5 md:w-6 md:h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold">Billing & Plan</h1>
            <p className="text-xs md:text-sm text-muted-foreground">Manage your subscription, add-ons, and billing</p>
          </div>
        </div>

        {/* ═══ Current Subscription ═══ */}
        <Card className={hasActiveSubscription ? 'border-green-200 bg-green-50/30' : ''} data-testid="current-plan-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              {hasActiveSubscription ? (
                <><CheckCircle className="w-5 h-5 text-green-600" />Your Current Plan</>
              ) : 'Subscription Status'}
            </CardTitle>
            {hasActiveSubscription && subscription?.plan_name && (
              <CardDescription className="text-base font-medium text-foreground">
                {subscription.plan_name} Plan
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              {getStatusBadge(subscription?.billing_status, hasActiveSubscription)}
              {!subscription?.is_comped && (subscription?.is_founder ? (
                <Badge className="bg-amber-100 text-amber-700 gap-1" data-testid="founder-badge">
                  <Sparkles className="w-3 h-3" />Founder Discount Active
                </Badge>
              ) : subscription?.is_beta && (
                <Badge className="bg-purple-100 text-purple-700 gap-1" data-testid="beta-badge">
                  <Sparkles className="w-3 h-3" />Beta Discount Active
                </Badge>
              ))}
              {subscription?.free_months_available > 0 && (
                <Badge className="bg-green-100 text-green-700 gap-1">
                  <Gift className="w-3 h-3" />{subscription.free_months_available} free month{subscription.free_months_available > 1 ? 's' : ''} available
                </Badge>
              )}
              {subscription?.cancel_at_period_end && <Badge variant="destructive">Cancels at period end</Badge>}
            </div>

            {hasActiveSubscription && !subscription?.is_comped && (
              <div className="grid gap-3 text-sm bg-white/60 rounded-lg p-4 border">
                {subscription.plan_price && (
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Base price:</span>
                    <span className="font-medium">
                      {subscription.plan_interval === 'year'
                        ? `$${(subscription.plan_price / 100).toFixed(0)}/year`
                        : `$${(subscription.plan_price / 100).toFixed(0)}/month`}
                    </span>
                  </div>
                )}
                {(subscription?.is_beta || subscription?.is_founder) && subscription.beta_discount_amount > 0 && (
                  <div className={`flex justify-between items-center ${subscription?.is_founder ? 'text-amber-600' : 'text-purple-600'}`}>
                    <span>{subscription?.is_founder ? 'Founder' : 'Beta'} discount:</span>
                    <span className="font-medium">-${(subscription.beta_discount_amount / 100).toFixed(0)}/month</span>
                  </div>
                )}
                {(subscription?.is_beta || subscription?.is_founder) && subscription.discounted_price !== null && (
                  <div className="flex justify-between items-center text-green-600 font-semibold border-t pt-2">
                    <span>You pay:</span>
                    <span className="text-lg">
                      {subscription.plan_interval === 'year'
                        ? `$${(subscription.discounted_price / 100).toFixed(0)}/year`
                        : `$${(subscription.discounted_price / 100).toFixed(0)}/month`}
                    </span>
                  </div>
                )}
                {!(subscription?.is_beta || subscription?.is_founder) && subscription.plan_price && (
                  <div className="flex justify-between items-center font-semibold border-t pt-2">
                    <span>You pay:</span>
                    <span className="text-lg">
                      {subscription.plan_interval === 'year'
                        ? `$${(subscription.plan_price / 100).toFixed(0)}/year`
                        : `$${(subscription.plan_price / 100).toFixed(0)}/month`}
                    </span>
                  </div>
                )}
                {subscription.current_period_end && (
                  <div className="flex justify-between items-center border-t pt-2" data-testid="renewal-date-display">
                    <span className="text-muted-foreground">
                      Renews {subscription.plan_interval === 'year' ? 'yearly' : 'monthly'} on:
                    </span>
                    <span className="font-medium">
                      {new Date(subscription.current_period_end).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </span>
                  </div>
                )}
              </div>
            )}

            {subscription?.billing_managed_in_stripe && hasPerm('settings.company') && (
              <Button variant="outline" onClick={handleManageBilling} disabled={portalLoading} className="mt-2" data-testid="manage-billing-stripe-btn">
                {portalLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                Manage Billing in Stripe
              </Button>
            )}

            {subscription?.is_comped && (
              <div className="flex items-center gap-2 mt-3 px-3 py-2.5 rounded-md bg-blue-50 border border-blue-200 text-blue-800 text-sm" data-testid="comped-billing-notice">
                <Shield className="w-4 h-4 shrink-0" />
                <span>This plan is managed by an administrator. Billing is not handled through Stripe.</span>
              </div>
            )}

            {!hasActiveSubscription && (
              <p className="text-sm text-muted-foreground pt-2">
                {subscription?.billing_status === 'trial'
                  ? "You're on a free trial. Choose a plan below to continue."
                  : "You don't have an active subscription. Choose a plan below."}
              </p>
            )}
          </CardContent>
        </Card>

        {/* ═══ Plan Options ═══ */}
        <div data-testid="plan-options-section">
          <h2 className="text-xl font-bold mb-2">{hasActiveSubscription ? 'Change Plan' : 'Choose a Plan'}</h2>
          <p className="text-sm text-muted-foreground mb-5">Select a product family and billing interval.</p>

          {/* ── Product family toggle ── */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <div className="inline-flex rounded-lg border p-0.5 bg-muted/40" data-testid="family-toggle">
              <button
                onClick={() => setSelectedFamily('crm')}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  selectedFamily === 'crm' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="family-toggle-crm"
              >
                <Users className="w-3.5 h-3.5" /> Inventory + CRM
              </button>
              <button
                onClick={() => setSelectedFamily('inventory')}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  selectedFamily === 'inventory' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="family-toggle-inventory"
              >
                <Boxes className="w-3.5 h-3.5" /> Inventory Only
              </button>
            </div>

            {/* ── Billing interval toggle ── */}
            <div className="inline-flex rounded-full border p-0.5 bg-muted/40" data-testid="interval-toggle">
              <button
                onClick={() => setBillingInterval('monthly')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                  billingInterval === 'monthly' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="interval-toggle-monthly"
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingInterval('yearly')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all relative ${
                  billingInterval === 'yearly' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="interval-toggle-yearly"
              >
                Yearly
                <span className="absolute -top-2 -right-2 text-[9px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-1.5">Save</span>
              </button>
            </div>
          </div>

          {/* ── Pricing cards ── */}
          <div className="grid gap-4 md:grid-cols-3" data-testid="pricing-cards-grid">
            {(FAMILY_ORDER[selectedFamily] || []).map((tierId) => {
              const tier = pricing.find((t) => t.tier_id === tierId);
              if (!tier) return null;
              const features = tier.features || [];
              const action = getTierAction(tier.tier_id);
              const isCurrentPlan = action === 'current';
              const hasDiscount = !subscription?.is_comped && (subscription?.is_beta || subscription?.is_founder);
              const isYearly = billingInterval === 'yearly';
              const discountPerUnit = hasDiscount ? (subscription.beta_discount_amount || 1000) : 0;

              // Monthly view: show monthly price. Yearly view: show effective monthly price.
              const rawMonthly = tier.price_monthly;
              const rawYearly = tier.price_yearly;
              const effectiveMonthlyFromYearly = Math.round(rawYearly / 12);

              const primaryPrice = isYearly ? effectiveMonthlyFromYearly : rawMonthly;
              const primaryDiscounted = primaryPrice - discountPerUnit;
              const annualDiscounted = rawYearly - discountPerUnit * 12;
              const annualSavings = (rawMonthly * 12) - rawYearly;
              const isPopular = tierId.includes('growth');

              return (
                <Card
                  key={tier.tier_id}
                  className={`relative transition-all ${isPopular ? 'border-primary shadow-lg' : ''} ${isCurrentPlan ? 'ring-2 ring-green-500 bg-green-50/30' : ''}`}
                  data-testid={`pricing-card-${tier.tier_id}`}
                >
                  {isCurrentPlan && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-green-600 text-white gap-1"><CheckCircle className="w-3 h-3" />Current Plan</Badge>
                    </div>
                  )}
                  <CardHeader className="pt-6">
                    {isPopular && !isCurrentPlan && <Badge className="w-fit mb-2">Most Popular</Badge>}
                    <CardTitle>{tier.name}</CardTitle>
                    <div className="space-y-1">
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-3xl font-bold" data-testid={`price-${tier.tier_id}`}>
                          ${Math.round((hasDiscount ? primaryDiscounted : primaryPrice) / 100)}
                        </span>
                        <span className="text-muted-foreground">/mo</span>
                      </div>
                      {isYearly && (
                        <div className="space-y-0.5">
                          <p className="text-xs text-muted-foreground">
                            {fmt(hasDiscount ? annualDiscounted : rawYearly)} billed annually
                          </p>
                          {annualSavings > 0 && (
                            <p className="text-xs font-semibold text-emerald-600" data-testid={`savings-${tier.tier_id}`}>
                              Save {fmt(annualSavings)}/year
                            </p>
                          )}
                        </div>
                      )}
                      {hasDiscount && (
                        <p className={`text-sm ${subscription?.is_founder ? 'text-amber-600' : 'text-purple-600'}`}>
                          <span className="line-through text-muted-foreground mr-2">${Math.round(primaryPrice / 100)}/mo</span>
                          {subscription?.is_founder ? 'Founder' : 'Beta'} discount applied!
                        </p>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ul className="space-y-2">
                      {features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />{f}
                        </li>
                      ))}
                    </ul>

                    {action === 'subscribe' && (
                      <Button className="w-full" variant={isPopular ? 'default' : 'outline'} onClick={() => handleSubscribe(tier.tier_id)} disabled={checkoutLoading !== null} data-testid={`subscribe-btn-${tier.tier_id}`}>
                        {checkoutLoading === tier.tier_id && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Get Started
                      </Button>
                    )}
                    {action === 'current' && (
                      <Button className="w-full" variant="secondary" disabled data-testid={`current-btn-${tier.tier_id}`}>
                        <CheckCircle className="w-4 h-4 mr-2 text-green-600" />Current Plan
                      </Button>
                    )}
                    {action === 'switch_interval' && hasPerm('settings.company') && (
                      <Button
                        className="w-full"
                        variant="outline"
                        onClick={() => handleChangePlan(tier.tier_id, billingInterval)}
                        disabled={changePlanLoading !== null}
                        data-testid={`switch-interval-btn-${tier.tier_id}`}
                      >
                        {changePlanLoading === `${tier.tier_id}_${billingInterval === 'yearly' ? 'year' : 'month'}` ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : null}
                        {isYearly ? 'Switch to Yearly' : 'Switch to Monthly'}
                      </Button>
                    )}
                    {action === 'upgrade' && hasPerm('settings.company') && (
                      <Button className="w-full" onClick={() => handleChangePlan(tier.tier_id, billingInterval)} disabled={changePlanLoading !== null} data-testid={`upgrade-btn-${tier.tier_id}`}>
                        {changePlanLoading === `${tier.tier_id}_${billingInterval === 'yearly' ? 'year' : 'month'}` ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowUp className="w-4 h-4 mr-2" />}
                        Upgrade to {tier.name}
                      </Button>
                    )}
                    {action === 'downgrade' && hasPerm('settings.company') && (
                      <Button className="w-full" variant="outline" onClick={() => handleChangePlan(tier.tier_id, billingInterval)} disabled={changePlanLoading !== null} data-testid={`downgrade-btn-${tier.tier_id}`}>
                        {changePlanLoading === `${tier.tier_id}_${billingInterval === 'yearly' ? 'year' : 'month'}` ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowDown className="w-4 h-4 mr-2" />}
                        Downgrade to {tier.name}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* ═══ Add-ons ═══ */}
        <div data-testid="addons-section">
          <h2 className="text-xl font-bold mb-2">Add-ons</h2>
          <p className="text-sm text-muted-foreground mb-4">Enhance your plan with additional capabilities.</p>
          {hasAddonBypass && (
            <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium" data-testid="addon-bypass-notice">
              <Shield className="w-3.5 h-3.5 shrink-0" />
              Admin override enabled for add-on testing
            </div>
          )}

          {/* Phone System row */}
          {phoneConfig && (
            <Card className={phoneEnabled ? 'border-primary/20' : ''} data-testid="phone-addon-card">
              <CardContent className="py-5">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${phoneEnabled ? 'bg-primary/10' : 'bg-muted'}`}>
                      <Phone className={`w-5 h-5 ${phoneEnabled ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold">{phoneConfig.name}</h3>
                        {phoneEnabled && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Active</Badge>}
                      </div>
                      <p className="text-sm font-medium mt-0.5">{fmt(phoneConfig.base_price_monthly)}/month</p>
                      <ul className="mt-1.5 space-y-0.5 text-xs text-muted-foreground">
                        <li>Includes {phoneConfig.included_lines} lines &middot; {phoneConfig.included_minutes} minutes</li>
                        <li>{fmt(phoneConfig.overage_per_minute)}/min overage &middot; {fmt(phoneConfig.extra_line_price_monthly)}/extra line</li>
                      </ul>
                    </div>
                  </div>

                  {/* Compact usage snapshot (active only, desktop) */}
                  {phoneEnabled && phoneSummary && (
                    <div className="hidden sm:flex items-center gap-5 text-xs text-muted-foreground shrink-0">
                      <div className="text-center">
                        <p className="text-base font-semibold text-foreground">{phoneSummary.active_lines}/{phoneSummary.included_lines}</p>
                        <p>Lines</p>
                      </div>
                      <div className="text-center">
                        <p className="text-base font-semibold text-foreground">{phoneSummary.minutes_used}/{phoneSummary.included_minutes}</p>
                        <p>Minutes</p>
                      </div>
                    </div>
                  )}

                  <div className="shrink-0">
                    {phoneEnabled ? (
                      <Button variant="outline" size="sm" onClick={handleOpenPhoneDrawer} data-testid="phone-addon-manage-btn">
                        Manage
                      </Button>
                    ) : isOwner ? (
                      <Button size="sm" onClick={handleEnablePhone} disabled={phoneEnabling} data-testid="phone-addon-enable-btn">
                        {phoneEnabling ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}
                        Add Phone System
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" disabled>Contact Owner</Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

            {/* StockBot / AI Actions Add-on */}
            <Card className={aiStatus?.enabled ? 'border-primary/20' : ''} data-testid="stockbot-addon-card">
              <CardContent className="py-5">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${aiStatus?.enabled ? 'bg-primary/10' : 'bg-muted'}`}>
                      <Brain className={`w-5 h-5 ${aiStatus?.enabled ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold">StockBot — AI Actions</h3>
                        {aiStatus?.enabled && (
                          <Badge className="bg-emerald-100 text-emerald-700 text-[10px]" data-testid="stockbot-active-badge">
                            {aiStatus.tier === 'pro' ? 'Pro' : 'Lite'}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 max-w-md">
                        AI operator in Messages — propose inventory moves, create POs, answer questions. All actions require approval before execution.
                      </p>
                      {/* Tier pricing */}
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span className={`font-medium ${aiStatus?.tier === 'lite' ? 'text-primary' : 'text-muted-foreground'}`}>
                          Lite $44/mo
                          <span className="text-[10px] font-normal ml-1">(200 credits)</span>
                        </span>
                        <span className="text-muted-foreground/30">|</span>
                        <span className={`font-medium ${aiStatus?.tier === 'pro' ? 'text-primary' : 'text-muted-foreground'}`}>
                          Pro $124/mo
                          <span className="text-[10px] font-normal ml-1">(800 credits)</span>
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Usage snapshot (when active) */}
                  {aiStatus?.enabled && (
                    <div className="hidden sm:flex items-center gap-5 text-xs text-muted-foreground shrink-0">
                      <div className="text-center">
                        <p className="text-base font-semibold text-foreground" data-testid="stockbot-usage-display">
                          {aiStatus.units_used}/{aiStatus.units_included}
                        </p>
                        <p>Credits</p>
                      </div>
                      {aiStatus.warning_level && aiStatus.warning_level !== 'ok' && (
                        <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-600">
                          {aiStatus.warning_level === 'exhausted' || aiStatus.warning_level === 'exceeded' ? 'Exhausted' : aiStatus.warning_level === 'critical' ? 'Almost Out' : 'Running Low'}
                        </Badge>
                      )}
                    </div>
                  )}

                  <div className="shrink-0">
                    {aiStatus?.enabled ? (
                      <div className="flex flex-col gap-1.5">
                        <Button
                          variant="outline" size="sm"
                          onClick={() => handleAiChangeTier(aiStatus.tier === 'pro' ? 'lite' : 'pro')}
                          disabled={aiEnabling}
                          data-testid="stockbot-change-tier-btn"
                        >
                          {aiEnabling ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : aiStatus.tier === 'pro' ? <ArrowDown className="w-3.5 h-3.5 mr-1" /> : <ArrowUp className="w-3.5 h-3.5 mr-1" />}
                          {aiStatus.tier === 'pro' ? 'Downgrade to Lite' : 'Upgrade to Pro'}
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          className="text-destructive hover:text-destructive text-xs"
                          onClick={handleAiDisable}
                          disabled={aiEnabling}
                          data-testid="stockbot-disable-btn"
                        >
                          Disable
                        </Button>
                      </div>
                    ) : isOwner ? (
                      <div className="flex flex-col gap-1.5">
                        <Button size="sm" onClick={() => handleAiEnable('pro')} disabled={aiEnabling} data-testid="stockbot-enable-pro-btn">
                          {aiEnabling ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}
                          Enable Pro — $124/mo
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleAiEnable('lite')} disabled={aiEnabling} data-testid="stockbot-enable-lite-btn">
                          Enable Lite — $44/mo
                        </Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" disabled>Contact Owner</Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

        {/* ═══ Billing Info ═══ */}
        <Card data-testid="billing-info-card">
          <CardHeader>
            <CardTitle className="text-lg">Billing Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-muted-foreground space-y-2">
              <p>Plans are available in monthly or yearly billing. Cancel anytime.</p>
              <p>Plan and interval changes take effect immediately with prorated charges.</p>
              {!subscription?.is_comped && (subscription?.is_beta || subscription?.is_founder) && (
                <p className={subscription?.is_founder ? 'text-amber-600' : 'text-purple-600'}>
                  Your {subscription?.is_founder ? 'Founder' : 'Beta'} discount of ${(subscription.beta_discount_amount / 100).toFixed(0)}/month applies to all plans forever!
                </p>
              )}
              {subscription?.free_months_available > 0 && (
                <p className="text-green-600">
                  You have {subscription.free_months_available} free month(s) from referrals that will be applied to upcoming invoices.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Phone drawer */}
      <PhoneAddonDrawer
        open={phoneDrawerOpen}
        onClose={() => setPhoneDrawerOpen(false)}
        summary={phoneSummary}
        billingHistory={phoneBillingHistory}
        historyLoading={phoneHistoryLoading}
        shadowData={phoneShadowData}
        onDisable={handleDisablePhone}
        disabling={phoneDisabling}
      />
    </Layout>
  );
};
